import { Seo } from "@/components/Seo";
import { useState, useEffect } from "react";
import { Link } from "react-router-dom";
import { MapPin, Building2, ExternalLink, FileDown, Lock } from "lucide-react";
import api, { fileUrl } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";

const TYPES = [
  { key: "all", label: "All" },
  { key: "internship", label: "Internships" },
  { key: "clerkship", label: "Clerkships" },
  { key: "associate", label: "Associate" },
  { key: "government", label: "Government" },
  { key: "judiciary", label: "Judiciary" },
  { key: "exam", label: "Exams" },
];

export default function Careers() {
  const { user } = useAuth();
  const [vacancies, setVacancies] = useState([]);
  const [type, setType] = useState("all");
  const [authModal, setAuthModal] = useState(false);

  useEffect(() => {
    const params = type === "all" ? "" : `?type=${type}`;
    api.get(`/vacancies${params}`).then(({ data }) => setVacancies(data)).catch(() => {});
  }, [type]);

  const openResource = (url) => {
    if (!user) {
      setAuthModal(true);
      return;
    }
    window.open(url, "_blank", "noopener,noreferrer");
  };

  return (
    <div className="max-w-6xl mx-auto px-5 sm:px-8 py-12" data-testid="careers-page">
      <Seo title="Legal Careers, Internships & Judiciary Vacancies | JusticeVerse" description="Discover curated legal jobs, internships, clerkships and judiciary vacancies across India on JusticeVerse Careers." />
      <span className="font-mono text-xs uppercase tracking-[0.25em] text-gold">Module 07</span>
      <h1 className="font-serif text-4xl sm:text-5xl font-light text-ink mt-3">Careers &amp; Internships</h1>
      <p className="text-inkmuted mt-4 max-w-2xl">
        Internships, judicial clerkships, associate positions, government vacancies and exam notifications — view the official notification or download the details.
      </p>

      <div className="flex flex-wrap gap-2 mt-10">
        {TYPES.map((t) => (
          <button
            key={t.key}
            onClick={() => setType(t.key)}
            data-testid={`career-filter-${t.key}`}
            className={`rounded-full px-5 py-2 text-sm font-medium border transition-all ${
              type === t.key ? "bg-btn text-btnink border-ink" : "border-hairline text-inkmuted hover:text-ink"
            }`}
          >
            {t.label}
          </button>
        ))}
      </div>

      <div className="grid md:grid-cols-2 gap-5 mt-10">
        {vacancies.map((v) => (
          <div key={v.id} className="glass-card rounded-2xl p-8 flex flex-col" data-testid="vacancy-card">
            <div className="flex items-center gap-3 text-inkmuted text-sm">
              <Building2 className="w-4 h-4 text-gold" /> {v.organization}
              <span className="text-inkfaint">·</span>
              <MapPin className="w-4 h-4" /> {v.location}
            </div>
            <h3 className="font-serif text-2xl text-ink mt-4">{v.title}</h3>
            <p className="text-sm text-inkmuted leading-relaxed mt-3 flex-1 whitespace-pre-wrap">{v.description}</p>
            <div className="flex flex-wrap gap-2 mt-5">
              {(v.tags || []).map((tag) => (
                <span key={tag} className="font-mono text-[10px] uppercase tracking-[0.15em] border border-hairline text-inkmuted px-3 py-1 rounded-full">{tag}</span>
              ))}
            </div>
            <div className="flex items-center justify-between gap-3 mt-6 pt-6 border-t border-hairline">
              <span className="font-serif text-xl gold-text">{v.stipend || "As per norms"}</span>
              <div className="flex items-center gap-2">
                {v.link_url && (
                  <button
                    onClick={() => openResource(v.link_url)}
                    data-testid="vacancy-link-btn"
                    className="rounded-full bg-btn text-btnink hover:bg-btnhover px-5 py-2 text-xs font-semibold uppercase tracking-wider transition-all flex items-center gap-2"
                  >
                    <ExternalLink className="w-3.5 h-3.5" /> View Details
                  </button>
                )}
                {v.attachment_url && (
                  <button
                    onClick={() => openResource(fileUrl(v.attachment_url))}
                    data-testid="vacancy-pdf-btn"
                    className="rounded-full border border-hairline text-ink hover:bg-surface2 px-5 py-2 text-xs font-semibold uppercase tracking-wider transition-all flex items-center gap-2"
                  >
                    <FileDown className="w-3.5 h-3.5" /> PDF
                  </button>
                )}
                {!v.link_url && !v.attachment_url && (
                  <span className="font-mono text-[10px] uppercase tracking-[0.15em] text-inkfaint">Details coming soon</span>
                )}
              </div>
            </div>
          </div>
        ))}
      </div>

      {authModal && (
        <div
          className="fixed inset-0 z-[60] bg-canvas/80 backdrop-blur-sm flex items-center justify-center p-4"
          onClick={() => setAuthModal(false)}
          data-testid="careers-auth-modal"
        >
          <div className="glass-card rounded-3xl max-w-sm w-full p-8 text-center" onClick={(e) => e.stopPropagation()}>
            <div className="w-14 h-14 rounded-full border border-gold/40 flex items-center justify-center mx-auto">
              <Lock className="w-6 h-6 text-gold" />
            </div>
            <h3 className="font-serif text-2xl text-ink mt-5">Sign in to view this opening</h3>
            <p className="text-sm text-inkmuted mt-2">
              Create a free account or sign in to open the official notification and download vacancy PDFs.
            </p>
            <div className="flex flex-col gap-3 mt-7">
              <Link
                to="/register"
                data-testid="careers-auth-register-btn"
                className="rounded-full bg-btn text-btnink hover:bg-btnhover px-6 py-3 text-sm font-semibold uppercase tracking-wider"
              >
                Create Free Account
              </Link>
              <Link
                to="/login"
                data-testid="careers-auth-login-btn"
                className="rounded-full bg-surface border border-hairline text-ink hover:bg-surface2 px-6 py-3 text-sm font-semibold uppercase tracking-wider"
              >
                Sign In
              </Link>
              <button onClick={() => setAuthModal(false)} className="text-inkfaint hover:text-ink text-sm mt-1">Maybe later</button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
